/**
 * アプリケーション設定（実行時の設定値を管理）
 */

import { Injectable } from '@angular/core';
import { STORAGE_KEYS } from '../constants';

/** アプリケーションの実行時設定モデル */
export interface AppSettings {
  /** セッション検証後に決定される報告者 ID */
  reporterId: string;

  /** Authorization ヘッダーを付与するか */
  tokenEnabled: boolean;

  /** Bearer Token 本体 */
  tokenValue: string;

  /** コピー時にヘッダーを含めるか */
  copySelectionWithHeaders: boolean;
}

@Injectable({ providedIn: 'root' })
export class AppConfigService {
  private static readonly defaultCopySelectionWithHeaders = true;

  /** localStorage 読み込み前の初期設定値 */
  private settings: AppSettings = {
    reporterId: '',
    tokenEnabled: false,
    tokenValue: '',
    copySelectionWithHeaders: AppConfigService.defaultCopySelectionWithHeaders,
  };

  constructor() {
    this.loadFromStorage();
  }

  /** localStorageから設定を復元 */
  private loadFromStorage(): void {
    try {
      const reporterId = localStorage.getItem(STORAGE_KEYS.REPORTER_ID);
      if (reporterId) {
        this.settings.reporterId = reporterId;
      }
      const tokenEnabled = localStorage.getItem(STORAGE_KEYS.TOKEN_ENABLED);
      if (tokenEnabled !== null) {
        this.settings.tokenEnabled = tokenEnabled === 'true';
      }
      const tokenValue = localStorage.getItem(STORAGE_KEYS.TOKEN_VALUE);
      if (tokenValue !== null) {
        this.settings.tokenValue = tokenValue;
      }
      const copySelectionWithHeaders = localStorage.getItem(STORAGE_KEYS.COPY_SELECTION_WITH_HEADERS);
      if (copySelectionWithHeaders !== null) {
        this.settings.copySelectionWithHeaders = copySelectionWithHeaders === 'true';
      }
    } catch {
      // localStorage アクセス不可時は初期値のまま
    }
  }

  /** 設定を保存 */
  saveSettings(partial: Partial<AppSettings>): void {
    Object.assign(this.settings, partial);
    try {
      localStorage.setItem(STORAGE_KEYS.REPORTER_ID, this.settings.reporterId);
      localStorage.setItem(STORAGE_KEYS.TOKEN_ENABLED, String(this.settings.tokenEnabled));
      localStorage.setItem(STORAGE_KEYS.TOKEN_VALUE, this.settings.tokenValue);
      localStorage.setItem(
        STORAGE_KEYS.COPY_SELECTION_WITH_HEADERS,
        String(this.settings.copySelectionWithHeaders),
      );
    } catch {
      // localStorage 書き込み失敗時は無視
    }
  }

  /** 認証トークン使用フラグ */
  get tokenEnabled(): boolean {
    return this.settings.tokenEnabled;
  }

  /** 現在使用中の reporterId */
  get reporterId(): string {
    return this.settings.reporterId;
  }

  /** 現在設定されているトークン値 */
  get tokenValue(): string {
    return this.settings.tokenValue;
  }

  /** コピー設定の現在値 */
  get copySelectionWithHeaders(): boolean {
    return this.settings.copySelectionWithHeaders;
  }

  /** コピー設定を初期値へ戻し、保存済みキーも削除する */
  resetCopySelectionWithHeaders(): void {
    this.settings.copySelectionWithHeaders = AppConfigService.defaultCopySelectionWithHeaders;
    try {
      localStorage.removeItem(STORAGE_KEYS.COPY_SELECTION_WITH_HEADERS);
    } catch {
      // localStorage 書き込み失敗時は無視
    }
  }

  /** 現在の設定のコピーを取得 */
  getSettings(): AppSettings {
    return { ...this.settings };
  }
}
